import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { Icon } from '../utils/icons';
import { useFamilyStore } from '../store/familyStore';
import { useCurrentUser } from '../hooks/useCurrentUser';
import { canShowFamilyMode } from '../utils/deviceDetection';
import { getDesignTokens } from '../utils/designTokens';
import ConfirmDialog from './ConfirmDialog';

interface UserMenuProps {
  isDarkMode?: boolean;
  isEmbedded?: boolean; // When true, shows content without sliding menu
  hideButton?: boolean; // When true, hides the button (for external control)
  externalOpen?: boolean; // External control for menu open state
  onExternalToggle?: (open: boolean) => void; // Callback for external toggle
}

export default function UserMenu({ 
  isDarkMode = false, 
  isEmbedded = false,
  hideButton = false,
  externalOpen,
  onExternalToggle
}: UserMenuProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const isOpen = externalOpen !== undefined ? externalOpen : internalOpen;
  const setIsOpen = onExternalToggle || setInternalOpen;
  const menuRef = useRef<HTMLDivElement>(null);
  const [showSignOutConfirm, setShowSignOutConfirm] = useState(false);
  const { signOut, signOutFromAllDevices, isFamilyMode, enterFamilyMode, exitFamilyMode } = useAuthStore();
  const { family } = useFamilyStore();
  const navigate = useNavigate();
  const showFamilyModeToggle = canShowFamilyMode();
  const { avatar: userAvatar, initials: userInitials, color: userColor, displayName: userDisplayName } = useCurrentUser();

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: Event) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isOpen, setIsOpen]);

  const handleSignOutThisDevice = () => {
    signOut();
    navigate('/login');
    setIsOpen(false);
  };

  const handleSignOutAllDevices = async () => {
    await signOutFromAllDevices();
    navigate('/login');
    setIsOpen(false);
    setShowSignOutConfirm(false);
  };

  const handleFamilyClick = () => {
    navigate('/family');
    setIsOpen(false);
  };

  const handleChangePasswordClick = () => {
    navigate('/change-password');
    setIsOpen(false);
  };

  const handleWhatsNewClick = () => {
    navigate('/whats-new');
    setIsOpen(false);
  };

  // Get tokens for both embedded and non-embedded modes
  const tokens = getDesignTokens(isDarkMode);

  // Embedded mode - show content directly without sliding menu
  if (isEmbedded) {
    return (
      <div className="space-y-4">
        {/* User Info */}
        <div className={`flex items-center gap-3 pb-4 ${tokens.border.light} border-b`}>
          <div className="relative">
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center text-white text-base font-bold"
              style={{ backgroundColor: userColor }}
            >
              {!userAvatar && userInitials}
            </div>
            {userAvatar && (
              <img
                src={userAvatar}
                alt={userDisplayName}
                className="absolute inset-0 w-10 h-10 rounded-full object-cover"
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
            )}
          </div>
          <div>
            <div className={`${tokens.text.primary} font-semibold text-base`}>
              {userDisplayName}
            </div>
            <div className={`${tokens.text.muted} text-xs`}>
              {family?.name || 'Family'}
            </div>
          </div>
        </div>

        {/* Family Mode toggle - tablet/landscape only */}
        {showFamilyModeToggle && (
          <div className={`${tokens.border.light} border rounded-landing px-4 py-3 mb-2 min-h-[48px] flex items-center justify-between gap-3`}>
            <span className={`${tokens.text.primary} font-medium`}>Family device mode</span>
            <button
              onClick={() => (isFamilyMode ? exitFamilyMode() : enterFamilyMode())}
              className={`min-w-[44px] min-h-[44px] rounded-landing font-medium transition-colors flex items-center justify-center px-3 ${isFamilyMode ? 'bg-primary-600 text-white' : `${tokens.card.bg} ${tokens.border.light} border ${tokens.text.primary}`}`}
              aria-label={isFamilyMode ? 'Exit family device mode' : 'Enter family device mode'}
            >
              {isFamilyMode ? 'Exit' : 'Enter'}
            </button>
          </div>
        )}

        {/* Menu Items */}
        <div className="space-y-2">
          <button
            onClick={handleFamilyClick}
            className={`w-full ${tokens.card.bg} ${tokens.text.primary} ${tokens.border.light} border rounded-landing px-4 py-3 text-left font-medium transition-colors min-h-[48px] flex items-center ${isDarkMode ? 'hover:bg-[rgba(255,255,255,0.05)]' : 'hover:bg-[rgba(0,0,0,0.02)]'}`}
          >
            <span className="mr-3 text-lg">👨‍👩‍👧‍👦</span>
            Family Management
          </button>
          <button
            onClick={handleChangePasswordClick}
            className={`w-full ${tokens.card.bg} ${tokens.text.primary} ${tokens.border.light} border rounded-landing px-4 py-3 text-left font-medium transition-colors min-h-[48px] flex items-center ${isDarkMode ? 'hover:bg-[rgba(255,255,255,0.05)]' : 'hover:bg-[rgba(0,0,0,0.02)]'}`}
          >
            <span className="mr-3 text-lg">🔒</span>
            Change Password
          </button>
          <button
            onClick={handleWhatsNewClick}
            className={`w-full ${tokens.card.bg} ${tokens.text.primary} ${tokens.border.light} border rounded-landing px-4 py-3 text-left font-medium transition-colors min-h-[48px] flex items-center ${isDarkMode ? 'hover:bg-[rgba(255,255,255,0.05)]' : 'hover:bg-[rgba(0,0,0,0.02)]'}`}
          >
            <span className="mr-3 text-lg">📋</span>
            What's New
          </button>
        </div>

        {/* Sign Out */}
        <div className={`pt-4 ${tokens.border.light} border-t space-y-2`}>
          <button
            onClick={handleSignOutThisDevice}
            className="w-full bg-red-600 hover:bg-red-700 text-white rounded-landing px-4 py-3 font-medium transition-colors min-h-[48px] flex items-center justify-center"
          >
            Sign Out
          </button>
          <button
            onClick={() => setShowSignOutConfirm(true)}
            className="w-full bg-red-700 hover:bg-red-800 text-white rounded-landing px-4 py-3 font-medium transition-colors min-h-[48px] flex items-center justify-center text-sm"
          >
            Sign Out from All Devices
          </button>
        </div>
      </div>
    );
  }

  const theme = {
    bg: tokens.card.bg,
    text: tokens.text.primary,
    hover: isDarkMode ? 'hover:bg-[rgba(255,255,255,0.05)]' : 'hover:bg-[rgba(0,0,0,0.02)]',
    border: tokens.border.light,
    overlay: isDarkMode ? 'bg-black/50' : 'bg-black/30',
      };

  return (
    <>
      {/* User Avatar Button - Hidden if hideButton is true */}
      {!hideButton && (
        <button
          onClick={() => setIsOpen(!isOpen)}
          className={`relative flex-shrink-0 focus:outline-none focus:ring-2 ${tokens.border.accent.replace('border-', 'focus:ring-')} rounded-full`}
          aria-label="User menu"
        >
        <div className="relative">
          <div
            className="w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-white text-xs sm:text-sm font-bold border-2 border-white shadow-lg"
            style={{ backgroundColor: userColor }}
          >
            {!userAvatar && userInitials}
          </div>
          {userAvatar && (
            <img
              src={userAvatar}
              alt={userDisplayName}
              className="absolute inset-0 w-9 h-9 sm:w-10 sm:h-10 rounded-full object-cover border-2 border-white shadow-lg"
              style={{ borderColor: userColor }}
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = 'none';
              }}
            />
          )}
        </div>
      </button>
      )}

      {/* Overlay */}
      {isOpen && (
        <div
          className={`fixed inset-0 ${theme.overlay} z-40 transition-opacity`}
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Sliding Menu */}
      <div
        ref={menuRef}
        className={`fixed top-0 right-0 h-full w-80 max-w-[90vw] ${theme.bg} ${theme.border} border-l shadow-2xl z-50 transform transition-transform duration-300 ease-in-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Menu Header */}
          <div className={`${theme.bg} ${theme.border} border-b p-3 flex items-center justify-between`}>
            <div className="flex items-center gap-3">
              <div className="relative">
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center text-white text-base font-bold"
                  style={{ backgroundColor: userColor }}
                >
                  {!userAvatar && userInitials}
                </div>
                {userAvatar && (
                  <img
                    src={userAvatar}
                    alt={userDisplayName}
                    className="absolute inset-0 w-10 h-10 rounded-full object-cover"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                )}
              </div>
              <div>
                <div className={`${theme.text} font-semibold text-base`}>
                  {userDisplayName}
                </div>
                <div className={`${theme.text} text-xs opacity-70`}>
                  {family?.name || 'Family'}
                </div>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className={`${theme.text} ${theme.hover} p-2 rounded-landing transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center`}
              aria-label="Close menu"
            >
              ✕
            </button>
          </div>

          {/* Menu Items */}
          <div className="flex-1 overflow-y-auto p-3">
            {showFamilyModeToggle && (
              <div className={`${theme.border} border rounded-landing px-4 py-3 mb-3 min-h-[48px] flex items-center justify-between gap-3`}>
                <span className={`${theme.text} font-medium`}>Family device mode</span>
                <button
                  onClick={() => (isFamilyMode ? exitFamilyMode() : enterFamilyMode())}
                  className={`min-w-[44px] min-h-[44px] rounded-landing font-medium transition-colors flex items-center justify-center px-3 ${isFamilyMode ? 'bg-primary-600 text-white' : `${theme.bg} ${theme.border} border ${theme.text}`}`}
                  aria-label={isFamilyMode ? 'Exit family device mode' : 'Enter family device mode'}
                >
                  {isFamilyMode ? 'Exit' : 'Enter'}
                </button>
              </div>
            )}
            <div className="space-y-2">
              <button
                onClick={handleFamilyClick}
                className={`w-full ${theme.bg} ${theme.text} ${theme.hover} ${theme.border} border rounded-landing px-4 py-3 text-left font-medium transition-colors min-h-[48px] flex items-center`}
              >
                <Icon name="family" size="md" className={`mr-3 ${theme.text}`} />
                Family Management
              </button>
              <button
                onClick={handleChangePasswordClick}
                className={`w-full ${theme.bg} ${theme.text} ${theme.hover} ${theme.border} border rounded-landing px-4 py-3 text-left font-medium transition-colors min-h-[48px] flex items-center`}
              >
                <Icon name="lock" size="md" className={`mr-3 ${theme.text}`} />
                Change Password
              </button>
            </div>
          </div>

          {/* Menu Footer */}
          <div className={`${theme.border} border-t p-3 space-y-2`}>
            <button
              onClick={handleSignOutThisDevice}
              className={`w-full bg-red-600 hover:bg-red-700 text-white rounded-landing px-4 py-3 font-medium transition-colors min-h-[48px] flex items-center justify-center`}
            >
              Sign Out
            </button>
            <button
              onClick={() => setShowSignOutConfirm(true)}
              className={`w-full bg-red-700 hover:bg-red-800 text-white rounded-landing px-4 py-3 font-medium transition-colors min-h-[48px] flex items-center justify-center text-sm`}
            >
              Sign Out from All Devices
            </button>
          </div>
        </div>
      </div>

      {/* Sign Out Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showSignOutConfirm}
        title="Sign Out from All Devices"
        message="This will sign you out from all devices where you're currently signed in. You'll need to sign in again on all devices. Continue?"
        confirmText="Sign Out Everywhere"
        cancelText="Cancel"
        onConfirm={handleSignOutAllDevices}
        onCancel={() => setShowSignOutConfirm(false)}
        isDestructive={true}
      />
    </>
  );
}
